import React from 'react';
import { useRouteError, isRouteErrorResponse, Link } from 'react-router-dom';

interface ErrorBoundaryProps {
  error?: Error;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = () => {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full text-center">
            <div className="mb-8">
              <h1 className="text-9xl font-bold text-gray-300">404</h1>
              <h2 className="text-2xl font-semibold text-gray-700 mb-4">Page Not Found</h2>
              <p className="text-gray-600 mb-8">
                Sorry, we couldn't find the page you're looking for. It might have been moved, deleted, or you entered the wrong URL.
              </p>
            </div>
            
            <div className="space-y-4">
              <Link
                to="/"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Go Home
              </Link>
              
              <div className="text-sm text-gray-500">
                <p>Or try one of these pages:</p>
                <div className="mt-2 space-x-4">
                  <Link to="/story" className="text-blue-600 hover:underline">Our Story</Link>
                  <Link to="/volunteer" className="text-blue-600 hover:underline">Volunteer</Link>
                  <Link to="/donate" className="text-blue-600 hover:underline">Donate</Link>
                  <Link to="/contact" className="text-blue-600 hover:underline">Contact</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <h1 className="text-4xl font-bold text-red-600 mb-4">Oops!</h1>
          <h2 className="text-xl font-semibold text-gray-700 mb-4">
            {error.status} {error.statusText}
          </h2>
          <p className="text-gray-600 mb-8">
            Something went wrong. Please try again later or contact support if the problem persists.
          </p>
          <Link
            to="/"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  // Handle other types of errors
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full text-center">
        <h1 className="text-4xl font-bold text-red-600 mb-4">Something went wrong</h1>
        <p className="text-gray-600 mb-8">
          An unexpected error occurred. Please try refreshing the page or contact support.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors mr-4"
        >
          Refresh Page
        </button>
        <Link
          to="/"
          className="inline-block bg-gray-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors"
        >
          Go Home
        </Link>
      </div>
    </div>
  );
};

export default ErrorBoundary;
